<template>
  <PopupContent
    v-show="showPopup"
    class="visualisation-popup"
    :style="popupStyle"
    :disable-chart="true"
    :is-mobile="true"
    :popup-details="popupDetails"
    @updated="updatePopupCardSize"
  />
</template>

<script lang="ts">
import Vue, { PropType } from 'vue'
import Component from 'vue-class-component'

import PopupContent from '@/components/public/Popup/PopupContent.vue'
import { VisualisationPopupDetails } from '@/types'

const POPUP_CARD_WIDTH = 300
const SHAPE_MARGIN = 10

const components = {
  PopupContent,
}

const props = {
  popupDetails: {
    type: Object as PropType<VisualisationPopupDetails | null>,
  },
}

// eslint-disable-next-line no-use-before-define
@Component<MobileVisualisationPopup>({ components })
export default class MobileVisualisationPopup extends Vue.extend({ props }) {
  popupCardHeight: number = 0

  get showPopup() {
    return this.popupDetails !== null
  }

  get isLandscape() {
    return this.$typedStore.public.display.isLandscape
  }

  get popupStyle() {
    return {
      top: `${this.position.top}px`,
      left: `${this.position.left}px`,
      width: `${POPUP_CARD_WIDTH}px`,
      transform: `scale(${this.transformCardScale})`,
      transformOrigin: 'left top',
    }
  }

  get viewportDimensions() {
    return {
      width: this.$typedStore.public.size.clientWidth,
      height: this.$typedStore.public.size.clientHeight,
    }
  }

  get maxAvailableViewportHeight() {
    // 90% of the available height so that the popup does not touch the top or bottom edges
    return this.viewportDimensions.height * 0.9
  }

  get maxAvailableViewportWidth() {
    // 90% of the available width so that the popup does not touch the left or right edges
    return this.viewportDimensions.width * 0.9
  }

  get minHorizontalEdgeGap() {
    return (this.viewportDimensions.width - this.maxAvailableViewportWidth) / 2
  }

  get minVerticalEdgeGap() {
    return (this.viewportDimensions.height - this.maxAvailableViewportHeight) / 2
  }

  get transformCardScale() {
    if (this.isLandscape) {
      if (this.popupCardHeight > this.maxAvailableViewportHeight) {
        const scale = this.maxAvailableViewportHeight / this.popupCardHeight
        return Math.min(scale, 1)
      }
    } else {
      if (POPUP_CARD_WIDTH > this.maxAvailableViewportWidth) {
        const scale = this.maxAvailableViewportWidth / POPUP_CARD_WIDTH
        return Math.min(scale, 1)
      }
    }

    return 1
  }

  get position() {
    if (!this.popupDetails) {
      return { left: 0, top: 0 }
    }

    const left = this.isLandscape
      ? this.calculateLandscapeLeftPosition(this.popupDetails.entityTopLeft.x)
      : this.calculatePortraitLeftPosition(this.popupDetails.entityCenter.x)

    const top = this.isLandscape
      ? this.calculateLandscapeTopPosition(this.popupDetails.entityCenter.y)
      : this.calculatePortraitTopPosition(this.popupDetails.entityTopLeft.y)

    return { left, top }
  }

  private calculateLandscapeLeftPosition(entityTopLeftX: number) {
    const leftBound = Math.max(
      this.visualisationContainerLeft() - POPUP_CARD_WIDTH,
      this.minHorizontalEdgeGap,
    )
    const idealLeft =
      this.visualisationContainerLeft() + entityTopLeftX - POPUP_CARD_WIDTH - SHAPE_MARGIN
    return Math.max(leftBound, idealLeft)
  }

  private calculatePortraitLeftPosition(entityCenterX: number) {
    let popupLeft = entityCenterX - POPUP_CARD_WIDTH / 2

    const popupRight = popupLeft + POPUP_CARD_WIDTH
    const viewportWidth = this.viewportDimensions.width

    // ensure the popup does not display outside the left or right of the viewport
    if (popupLeft < this.minHorizontalEdgeGap) {
      popupLeft = this.minHorizontalEdgeGap
    } else if (popupRight > viewportWidth - this.minHorizontalEdgeGap) {
      popupLeft = viewportWidth - this.minHorizontalEdgeGap - POPUP_CARD_WIDTH
    }
    return popupLeft
  }

  private calculateLandscapeTopPosition(entityCenterY: number) {
    let popupTop = entityCenterY - this.popupCardHeight / 2

    const upperBound = this.minVerticalEdgeGap
    const lowerBound = this.viewportDimensions.height - this.minVerticalEdgeGap
    const popupBottom = popupTop + this.popupCardHeight
    if (popupTop < upperBound) {
      popupTop = upperBound
    } else if (popupBottom > lowerBound) {
      popupTop = lowerBound - this.popupCardHeight
    }
    return popupTop
  }

  private calculatePortraitTopPosition(entityTopLeftY: number) {
    const upperBound = Math.max(
      this.visualisationContainerTop() - this.popupCardHeight,
      this.minVerticalEdgeGap,
    )
    const idealTop =
      this.visualisationContainerTop() + entityTopLeftY - this.popupCardHeight - SHAPE_MARGIN
    return Math.max(upperBound, idealTop)
  }

  private visualisationContainerBoundingClientRect() {
    return document.querySelector('#visualisationCanvasContainer')?.getBoundingClientRect()
  }

  private visualisationContainerLeft() {
    return this.visualisationContainerBoundingClientRect()?.left ?? 0
  }

  private visualisationContainerTop() {
    return this.visualisationContainerBoundingClientRect()?.top ?? 0
  }

  updatePopupCardSize(newValue: { clientHeight: number }) {
    this.popupCardHeight = newValue.clientHeight
  }
}
</script>

<style scoped>
.visualisation-popup {
  position: absolute;
  z-index: 4;
  width: 300px;
  opacity: 0.97;
}
</style>
